(() => {
  const normalize = (value = '') =>
    value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');

  class TiworksCollectionNavigator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.items = [...this.querySelectorAll('[data-tiworks-quick-filter]')];
      this.notice = this.querySelector('[data-tw-nav-notice]');

      this.onCollectionRerendered = this.onCollectionRerendered.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onResize = this.onResize.bind(this);

      this.bindTabs();
      this.bindItems();
      document.addEventListener('collection:rerendered', this.onCollectionRerendered);
      window.addEventListener('popstate', this.onPopState);
      window.addEventListener('resize', this.onResize, { passive: true });

      this.syncFromNativeFilters();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      document.removeEventListener('collection:rerendered', this.onCollectionRerendered);
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.resizeTimer);
    }

    bindTabs() {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activateTab(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();

          let next = index;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;

          this.tabs[next]?.focus();
          this.activateTab(this.tabs[next]);
        });
      });

      const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      if (selected) this.activateTab(selected, false);
    }

    bindItems() {
      this.items.forEach((item) => {
        item.addEventListener('click', () => this.applyQuickFilter(item));
      });
    }

    activateTab(tab, focus = false) {
      if (!tab) return;

      const targetId = tab.getAttribute('aria-controls');
      this.tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== targetId;
      });
      if (focus) tab.focus();

      window.requestAnimationFrame(() => {
        const activeItem = this.querySelector(`#${targetId} .tiworks-collection-nav__item.is-active`);
        this.ensureItemVisible(activeItem, 'auto');
      });
    }

    onCollectionRerendered() {
      this.syncFromNativeFilters();
    }

    onPopState() {
      window.requestAnimationFrame(() => this.syncFromNativeFilters());
    }

    onResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        const grids = [...this.querySelectorAll('.tiworks-collection-nav__grid')];
        if (window.innerWidth > 767) {
          grids.forEach((grid) => {
            grid.scrollLeft = 0;
          });
          return;
        }

        const activeItem = this.querySelector('.tiworks-collection-nav__item.is-active');
        this.ensureItemVisible(activeItem, 'auto');
      }, 120);
    }

    ensureItemVisible(item, behavior = 'smooth') {
      if (!item || window.innerWidth > 767) return;
      const scroller = item.closest('.tiworks-collection-nav__grid');
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

      const itemLeft = item.offsetLeft;
      const itemRight = itemLeft + item.offsetWidth;
      const viewLeft = scroller.scrollLeft;
      const viewRight = viewLeft + scroller.clientWidth;
      if (itemLeft >= viewLeft && itemRight <= viewRight) return;

      const target = itemLeft - (scroller.clientWidth - item.offsetWidth) / 2;
      scroller.scrollTo({ left: Math.max(0, target), behavior });
    }

    getFilterForm() {
      return document.querySelector('#CollectionFiltersForm');
    }

    getInputLabel(input) {
      const label = input.closest('label');
      if (!label) return input.value;

      const namedLabel = label.querySelector(
        '.m-facet--label, .m-facet--color-name, [class*="visually-hidden"]'
      );
      return namedLabel?.textContent?.replace(/\([^)]*\)\s*$/, '').trim() || input.value;
    }

    getWidgetLabel(input) {
      const widget = input.closest('.m-filter--widget');
      const title = widget?.querySelector('.m-filter--widget-title span, .m-filter--widget-title');
      return title?.textContent?.trim() || '';
    }

    findMatchingInput(item) {
      const form = this.getFilterForm();
      if (!form) return null;

      const expectedFilter = normalize(item.dataset.filterLabel);
      const expectedValue = normalize(item.dataset.filterValue || item.dataset.filterLabel);
      const inputs = [...form.querySelectorAll('input[type="checkbox"][name]')];

      const exact = inputs.find((input) => {
        const widgetLabel = normalize(this.getWidgetLabel(input));
        const inputValue = normalize(input.value);
        const inputLabel = normalize(this.getInputLabel(input));
        const filterMatches = !expectedFilter || widgetLabel === expectedFilter;
        const valueMatches = inputValue === expectedValue || inputLabel === expectedValue;
        return filterMatches && valueMatches;
      });

      if (exact) return exact;

      return inputs.find((input) => {
        const inputValue = normalize(input.value);
        const inputLabel = normalize(this.getInputLabel(input));
        return inputValue === expectedValue || inputLabel === expectedValue;
      });
    }

    getGroupInputs(input) {
      const widget = input?.closest('.m-filter--widget');
      if (!widget) return input ? [input] : [];
      return [...widget.querySelectorAll('input[type="checkbox"][name]')];
    }

    applyQuickFilter(item) {
      const input = this.findMatchingInput(item);
      if (!input || input.disabled) {
        this.showNotice();
        return;
      }

      this.hideNotice();
      const groupInputs = this.getGroupInputs(input);
      const checkedInputs = groupInputs.filter((candidate) => candidate.checked);
      const isOnlyActive = input.checked && checkedInputs.length === 1;

      // The top shortcuts are intentionally single-select inside one filter group.
      // Different groups remain combined. The native sidebar itself still supports full multi-select.
      groupInputs.forEach((candidate) => {
        candidate.checked = false;
      });
      if (!isOnlyActive) input.checked = true;

      this.syncFromNativeFilters();
      this.submitCurrentFilters(input);
    }

    submitCurrentFilters(input) {
      const form = input?.form || this.getFilterForm();
      const filterElement = form?.closest('collection-filters-form');

      // The quick navigator lives outside the AJAX-replaced collection section.
      // Calling the current custom element directly is more reliable than
      // dispatching a synthetic input event, which can target a newly-rendered
      // form before its delegated listener has finished reconnecting.
      if (form && filterElement && typeof filterElement.renderPage === 'function') {
        const searchParams = new URLSearchParams(new FormData(form)).toString();
        filterElement.renderPage(searchParams);
        return;
      }

      // Safe fallback for themes/browsers where the custom element is unavailable.
      input?.dispatchEvent(new Event('input', { bubbles: true }));
    }

    syncFromNativeFilters() {
      let activeItem = null;

      this.items.forEach((item) => {
        const input = this.findMatchingInput(item);
        const isActive = Boolean(input?.checked);
        const isUnavailable = !input || (input.disabled && !input.checked);
        const shouldHide = item.dataset.hideWhenEmpty === 'true' && Boolean(input) && input.disabled && !input.checked;

        item.hidden = shouldHide;
        item.classList.toggle('is-active', isActive);
        item.classList.toggle('is-unavailable', isUnavailable);
        item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        item.disabled = isUnavailable;

        const countNode = item.querySelector('[data-tiworks-filter-count]');
        if (countNode && input) {
          const sourceCount = input.closest('label')?.querySelector('.m-facet--product-count')?.textContent || '';
          const cleanCount = sourceCount.replace(/[()]/g, '').trim();
          if (cleanCount) {
            countNode.textContent = `${cleanCount} products`;
            countNode.hidden = false;
          }
        }

        if (isActive && !activeItem) activeItem = item;
      });

      const activePanel = activeItem?.closest('[role="tabpanel"]');
      const activeTab = this.tabs.find((tab) => tab.getAttribute('aria-controls') === activePanel?.id);
      if (activeTab) this.activateTab(activeTab, false);
      window.requestAnimationFrame(() => this.ensureItemVisible(activeItem, 'auto'));
    }

    showNotice() {
      if (!this.notice) return;
      this.notice.classList.add('is-visible');
      this.notice.focus({ preventScroll: true });
    }

    hideNotice() {
      this.notice?.classList.remove('is-visible');
    }
  }

  if (!customElements.get('tiworks-collection-navigator')) {
    customElements.define('tiworks-collection-navigator', TiworksCollectionNavigator);
  }
})();
